<template>
  <div class="dp-pet-selector d-inline-block">
    <label
      v-if="label"
      :for="resolvedSelectId"
      class="d-block mb-2"
    >{{ label }}</label>
    <div class="pet-control position-relative w-100">
      <div
        class="pet-trigger d-flex align-items-center w-100"
        aria-hidden="true"
      >
        <div
          v-if="selectedPet"
          class="pet-row d-flex align-items-center flex-grow-1"
        >
          <div
            class="pet-avatar d-inline-flex align-items-center justify-content-center bg-white border rounded-circle overflow-hidden flex-shrink-0"
          >
            <img
              v-if="selectedPet.photo"
              :src="selectedPet.photo"
              :alt="selectedPet.name"
              class="pet-avatar-img w-100 h-100"
            />
            <img
              v-else
              :src="placeholderAvatarFor(selectedPet)"
              alt=""
              class="pet-avatar-img pet-avatar-img--placeholder w-100 h-100"
            />
          </div>
          <div class="pet-info d-flex flex-column">
            <span class="pet-name font-weight-bold text-truncate">{{ selectedPet.name }}</span>
            <dp-membership-badge
              v-if="selectedPet.membership"
              :no-background="true"
              :tier-name="selectedPet.membership.tierName"
              :label="selectedPet.membership.label"
            />
          </div>
        </div>
        <div v-else class="flex-grow-1 text-left">{{ placeholder }}</div>
        <fa-icon
          :icon="['fas', 'chevron-down']"
          class="pet-chevron ml-3 flex-shrink-0"
        />
      </div>
      <select
        :id="resolvedSelectId"
        class="pet-select position-absolute w-100 h-100"
        :value="currentPetId"
        :aria-label="label || placeholder"
        @change="onChange"
      >
        <option v-if="!hasSelection" :value="null" disabled>{{ placeholder }}</option>
        <option v-for="pet in pets" :key="pet.petId" :value="pet.petId">{{ pet.name }}</option>
      </select>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

import DpMembershipBadge from '../MembershipBadge/MembershipBadge';

library.add(faChevronDown);

const PLACEHOLDER_DOG = '/images/avatar-dog.png';
const PLACEHOLDER_CAT = '/images/avatar-cat.png';

export default {
  name: 'DpPetSelector',
  components: { DpMembershipBadge },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    pets: {
      type: Array,
      required: true,
    },
    value: {
      type: [Number, String],
      required: false,
      default: null,
    },
    label: {
      type: String,
      required: false,
      default: null,
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Select a pet',
    },
    selectId: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    resolvedSelectId() {
      return this.selectId || `dp-pet-selector-${this._uid}`;
    },
    hasSelection() {
      return this.value !== null && this.value !== undefined;
    },
    currentPetId() {
      if (this.hasSelection) {
        return this.value;
      }
      return this.pets.length > 0 ? this.pets[0].petId : null;
    },
    selectedPet() {
      return (
        this.pets.find((pet) => String(pet.petId) === String(this.currentPetId)) || null
      );
    },
  },
  methods: {
    placeholderAvatarFor(pet) {
      const species = String(pet?.species || '').toLowerCase();
      return species === 'cat' ? PLACEHOLDER_CAT : PLACEHOLDER_DOG;
    },
    onChange(event) {
      const rawValue = event.target.value;
      const matched = this.pets.find((pet) => String(pet.petId) === String(rawValue));
      if (!matched) {
        return;
      }
      this.$emit('input', matched.petId);
      this.$emit('change', matched);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/_variables';

.dp-pet-selector {
  width: 280px;
  max-width: 100%;
  color: $neutral-700;
  font-size: 16px;
  line-height: 1.5;

  .pet-trigger {
    padding: 12px 16px;
    border: 1px solid $blue-800;
    border-radius: 8px;
    color: $blue-800;
    background-color: $dp-white;
  }

  .pet-select {
    inset: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: transparent;
    opacity: 0;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;

    &:focus,
    &:focus-visible {
      outline: none;
    }
    option {
      color: $neutral-700;
      background-color: $dp-white;
    }
  }

  .pet-control:hover .pet-trigger,
  .pet-control:focus-within .pet-trigger {
    background-color: $blue-50;
    box-shadow: 0 0 0 1px $blue-800;
  }
  .pet-row {
    gap: 8px;
    min-width: 0;
    text-align: left;
  }

  .pet-avatar {
    width: 48px;
    height: 48px;
    border-color: $neutral-400 !important;
  }

  .pet-avatar-img {
    object-fit: cover;

    &--placeholder {
      object-fit: contain;
      padding: 6px;
    }
  }

  .pet-info {
    gap: 2px;
    min-width: 0;
  }

  .pet-name {
    color: $blue-800;
  }

  .pet-chevron {
    width: 16px;
    height: 16px;
    color: $blue-800;
  }
}
</style>
